import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import { documentAPI, Document } from '../services/api';
import { ClipboardIcon, AlertIcon, ChatIcon, UploadIcon, FileTextIcon, UserIcon, HomeIcon } from '../components/Icons';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['dashboard', 'common']);
  const { getLocalizedPath } = useLanguage();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setError(t('upload.errors.invalidFormat'));
      return;
    }

    if (file.size > 15 * 1024 * 1024) {
      setError(t('upload.errors.fileTooLarge'));
      return;
    }

    setError('');
    setSelectedFile(file);
    setShowRoleModal(true);
  };

  const handleRoleSelection = async (role: 'tenant' | 'landlord') => {
    if (!selectedFile) return;

    setShowRoleModal(false);
    setUploading(true);

    try {
      const res = await documentAPI.upload(selectedFile, role, i18n.language);
      const createdDoc = (res as any)?.data?.document || (res as any)?.data || null;
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      if (createdDoc?.id) {
        // Check if this is a duplicate document
        if (createdDoc?.is_duplicate) {
          // Show info message about duplicate
          setError(createdDoc.message || t('upload.duplicateWarning'));
        }
        navigate(getLocalizedPath(`/documents/${createdDoc.id}`), { state: { document: createdDoc as Document } });
      } else {
        // Fallback if backend shape changes
        navigate(getLocalizedPath('/catalog'));
      }
    } catch (err: any) {
      const errorDetail = err.response?.data?.detail;
      const isTimeout = err.code === 'ECONNABORTED';
      if (isTimeout) {
        // Try to recover by redirecting to the most recent doc which is likely the upload we just made
        try {
          const list = await documentAPI.list();
          const latest = list.data.documents?.[0];
          if (latest?.id) {
            navigate(getLocalizedPath(`/documents/${latest.id}`), { state: { document: latest as Document } });
            setError(t('upload.timeoutWarning'));
            return;
          }
        } catch (listErr) {
          console.error('Timeout during upload and failed to load documents list:', listErr);
        }
      }
      const errorMessage = typeof errorDetail === 'string'
        ? errorDetail
        : errorDetail?.message || (isTimeout ? t('upload.errors.timeout') : t('upload.errors.uploadFailed'));
      setError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="container dashboard">
      {/* Role Selection Modal */}
      {showRoleModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2 className="modal-title">{t('roleSelection.title')}</h2>
            <p className="modal-subtitle">
              {t('roleSelection.subtitle')}
            </p>
            <div className="role-buttons">
              <button
                className="role-button tenant-button"
                onClick={() => handleRoleSelection('tenant')}
              >
                <div className="role-icon"><UserIcon size={32} /></div>
                <div className="role-title">{t('roleSelection.tenant')}</div>
                <div className="role-description">{t('roleSelection.tenantDesc')}</div>
              </button>
              <button
                className="role-button landlord-button"
                onClick={() => handleRoleSelection('landlord')}
              >
                <div className="role-icon"><HomeIcon size={32} /></div>
                <div className="role-title">{t('roleSelection.landlord')}</div>
                <div className="role-description">{t('roleSelection.landlordDesc')}</div>
              </button>
            </div>
            <button
              className="modal-close"
              onClick={() => {
                setShowRoleModal(false);
                setSelectedFile(null);
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
              }}
            >
              {t('roleSelection.cancel')}
            </button>
          </div>
        </div>
      )}

      <div className="dashboard-header">
        <h1 className="dashboard-title">{t('title')}</h1>
        <h2 className="dashboard-subtitle-main"><span className="dashboard-highlight">{t('titleHighlight')}</span></h2>
        <p className="dashboard-description">
          {t('description')}
        </p>
      </div>

      <div className="features-grid">
        <div className="feature-card">
          <div className="feature-icon">
            <ClipboardIcon size={32} />
          </div>
          <h3 className="feature-title">{t('features.analysis.title')}</h3>
          <p className="feature-description">{t('features.analysis.description')}</p>
        </div>
        <div className="feature-card">
          <div className="feature-icon">
            <AlertIcon size={32} />
          </div>
          <h3 className="feature-title">{t('features.risks.title')}</h3>
          <p className="feature-description">{t('features.risks.description')}</p>
        </div>
        <div className="feature-card">
          <div className="feature-icon">
            <ChatIcon size={32} />
          </div>
          <h3 className="feature-title">{t('features.questions.title')}</h3>
          <p className="feature-description">{t('features.questions.description')}</p>
        </div>
      </div>

      <div className="upload-section">
        <h2 className="upload-title">{t('upload.title')}</h2>
        <p className="upload-subtitle">
          {t('upload.subtitle')}
        </p>
        {error && <div className="error-message">{error}</div>}
        <div className="upload-zone" onClick={handleUploadClick}>
          <div className="upload-icon">
            <UploadIcon size={48} />
          </div>
          <div className="upload-text">
            {uploading ? t('upload.uploading') : t('upload.button')}
          </div>
          <div className="upload-formats">
            <span><FileTextIcon size={16} /> {t('upload.dropzone.formats')}</span>
            <span>· {t('upload.dropzone.maxSize')}</span>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            onChange={handleFileSelect}
            className="file-input"
            disabled={uploading}
          />
        </div>
      </div>

    </div>
  );
};

export default Dashboard;
